import * as React from 'react'

import { clsx } from 'clsx'

import { Card, Heading, Image, ImageIcon, Tag } from '@buffer-mono/popcorn'
import type { HeadingProps, TagColor } from '@buffer-mono/popcorn'

import styles from './ContentCard.module.css'

import type { JSX } from 'react'

type ContentCardData = {
  /** Headline shown on the card. */
  title: string
  /** Content type label rendered as a coloured tag, e.g. "Blog post". */
  category?: string
  /** Tag colour for the category. Defaults to a neutral gray. */
  categoryColor?: TagColor
  /** Preformatted publication-date label. The card renders the string as given. */
  publishedAtLabel?: string
  /** Thumbnail image URL. */
  imageUrl: string
  /** Accessible description for the thumbnail. Falls back to the title. */
  imageAlt?: string
}

const ContentCardContext = React.createContext<ContentCardData | null>(null)

function useContentCardContext(): ContentCardData {
  const context = React.useContext(ContentCardContext)
  if (!context) {
    throw new Error(
      'ContentCard sub-components must be used within ContentCard',
    )
  }
  return context
}

type RootProps = {
  content: ContentCardData
  className?: string
  children: React.ReactNode
}

const Root = ({ content, className, children }: RootProps): JSX.Element => {
  return (
    <ContentCardContext.Provider value={content}>
      <Card asChild>
        <article
          className={clsx(styles.card, className)}
          aria-label={content.title}
        >
          {children}
        </article>
      </Card>
    </ContentCardContext.Provider>
  )
}

Root.displayName = 'ContentCard'

type CategoryProps = {
  className?: string
}

const Category = ({ className }: CategoryProps): JSX.Element | null => {
  const content = useContentCardContext()

  if (!content.category) {
    return null
  }

  return (
    <Tag
      color={content.categoryColor ?? 'gray'}
      className={clsx(styles.category, className)}
    >
      {content.category}
    </Tag>
  )
}

Category.displayName = 'ContentCard.Category'

type PublishedAtProps = {
  className?: string
}

const PublishedAt = ({ className }: PublishedAtProps): JSX.Element | null => {
  const content = useContentCardContext()

  if (!content.publishedAtLabel) {
    return null
  }

  return (
    <p className={clsx(styles.publishedAt, className)}>
      {content.publishedAtLabel}
    </p>
  )
}

PublishedAt.displayName = 'ContentCard.PublishedAt'

type TitleProps = {
  size?: HeadingProps['size']
  /** Heading level, so a card fits the hierarchy of the section around it. */
  as?: Extract<HeadingProps['as'], 'h2' | 'h3' | 'h4'>
  /** Maximum number of visible lines before truncating. */
  maxLines?: number
  className?: string
}

const Title = ({
  size = 'sm',
  as = 'h3',
  maxLines = 3,
  className,
}: TitleProps): JSX.Element => {
  const content = useContentCardContext()
  const style: React.CSSProperties & Record<`--${string}`, number> = {
    '--line-clamp': maxLines,
  }

  return (
    <Heading
      as={as}
      size={size}
      className={clsx(styles.title, className)}
      style={style}
    >
      {content.title}
    </Heading>
  )
}

Title.displayName = 'ContentCard.Title'

type ThumbnailProps = {
  className?: string
}

const Thumbnail = ({ className }: ThumbnailProps): JSX.Element => {
  const content = useContentCardContext()
  const [hasError, setHasError] = React.useState(false)

  if (hasError || content.imageUrl.trim().length === 0) {
    return (
      <div className={clsx(styles.thumbnailFallback, className)}>
        <ImageIcon size={24} aria-hidden />
      </div>
    )
  }

  return (
    <div className={clsx(styles.thumbnailWrapper, className)}>
      <Image
        src={content.imageUrl}
        alt={content.imageAlt ?? content.title}
        className={styles.thumbnail}
        referrerPolicy="no-referrer"
        onError={() => setHasError(true)}
      />
    </div>
  )
}

Thumbnail.displayName = 'ContentCard.Thumbnail'

export const ContentCard = Object.assign(Root, {
  Category,
  PublishedAt,
  Thumbnail,
  Title,
})

export type { RootProps as ContentCardProps, ContentCardData }
